import { useState, useEffect, useRef } from 'react'
import {
  Grid,
  Box,
  Typography,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  Stack,
  ListSubheader,
  CircularProgress,
} from '@mui/material'
import {
  Assessment,
  Build,
  Inventory2,
  AttachMoney,
  Assignment,
  LocalShipping,
  Download,
  Refresh,
  Timeline,
  AccountTree,
  Speed,
  Schedule,
  TrendingDown,
  Science,
  Biotech,
  EventNote,
  PictureAsPdf,
  FilterList,
} from '@mui/icons-material'
import { PageHeader, Card, Button, LoadingSpinner } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'
import { useAuth } from '@context/AuthContext'
import { useBranding } from '@context/BrandingContext'

const reportTypes = [
  // Standard Reports
  {
    id: 'asset_summary',
    title: 'Asset Summary',
    description: 'Overview of all assets by status, category, and department',
    icon: <Assessment />,
    color: '#1B5E7A',
    endpoint: '/reports/data/asset_summary/',
    category: 'standard',
  },
  {
    id: 'maintenance_summary',
    title: 'Maintenance Report',
    description: 'Work order completion rates and maintenance metrics',
    icon: <Build />,
    color: '#10b981',
    endpoint: '/reports/data/maintenance_summary/',
    category: 'standard',
  },
  {
    id: 'work_order_analysis',
    title: 'Work Order Analysis',
    description: 'Detailed analysis of work orders by type and priority',
    icon: <Assignment />,
    color: '#3b82f6',
    endpoint: '/reports/data/work_order_analysis/',
    category: 'standard',
  },
  {
    id: 'inventory_summary',
    title: 'Inventory Status',
    description: 'Stock levels, usage trends, and reorder alerts',
    icon: <Inventory2 />,
    color: '#f59e0b',
    endpoint: '/reports/data/inventory_summary/',
    category: 'standard',
  },
  {
    id: 'cost_analysis',
    title: 'Cost Analysis',
    description: 'Maintenance costs breakdown by asset and category',
    icon: <AttachMoney />,
    color: '#ef4444',
    endpoint: '/reports/data/cost_analysis/',
    category: 'standard',
  },
  {
    id: 'vendor_performance',
    title: 'Vendor Performance',
    description: 'Vendor service ratings and contract compliance',
    icon: <LocalShipping />,
    color: '#8b5cf6',
    endpoint: '/reports/data/vendor_performance/',
    category: 'standard',
  },
  {
    id: 'vehicle_mileage_summary',
    title: 'Vehicle Mileage',
    description: 'Per-vehicle distance, service-due status, and mileage anomalies',
    icon: <Speed />,
    color: '#0e7490',
    endpoint: '/reports/data/vehicle_mileage_summary/',
    category: 'standard',
  },
  // Specialized Reports
  {
    id: 'downtime_analysis',
    title: 'Equipment Downtime',
    description: 'Downtime analysis by asset, department and category',
    icon: <TrendingDown />,
    color: '#dc2626',
    endpoint: '/reports/data/downtime_analysis/',
    category: 'specialized',
  },
  {
    id: 'pm_compliance',
    title: 'PM Compliance',
    description: 'Preventive maintenance completion rates and overdue tasks',
    icon: <Schedule />,
    color: '#0891b2',
    endpoint: '/reports/data/pm_compliance/',
    category: 'specialized',
  },
  {
    id: 'calibration_report',
    title: 'Calibration Report',
    description: 'Calibration records, compliance, and upcoming due dates',
    icon: <Speed />,
    color: '#7c3aed',
    endpoint: '/reports/data/calibration_report/',
    category: 'specialized',
  },
  {
    id: 'equipment_lifecycle',
    title: 'Equipment Lifecycle',
    description: 'Asset age analysis, replacement forecasts and warranties',
    icon: <Timeline />,
    color: '#059669',
    endpoint: '/reports/data/equipment_lifecycle/',
    category: 'specialized',
  },
  {
    id: 'departmental_costs',
    title: 'Section/Unit Costs',
    description: 'Maintenance costs broken down by department',
    icon: <AccountTree />,
    color: '#d97706',
    endpoint: '/reports/data/departmental_costs/',
    category: 'specialized',
  },
  {
    id: 'high_cost_assets',
    title: 'High Cost Assets',
    description: 'Assets with highest purchase and maintenance costs',
    icon: <AttachMoney />,
    color: '#be185d',
    endpoint: '/reports/data/high_cost_assets/',
    category: 'specialized',
  },
  // Oncology Reports
  {
    id: 'qa_compliance',
    title: 'QA Compliance',
    description: 'Radiation equipment QA records and pass rates',
    icon: <Science />,
    color: '#4f46e5',
    endpoint: '/reports/data/qa_compliance/',
    category: 'oncology',
  },
  {
    id: 'radiation_source_inventory',
    title: 'Source Inventory',
    description: 'Radiation source activity, decay tracking and leak tests',
    icon: <Biotech />,
    color: '#0d9488',
    endpoint: '/reports/data/radiation_source_inventory/',
    category: 'oncology',
  },
  {
    id: 'equipment_log_summary',
    title: 'Equipment Logs',
    description: 'LINAC beam time, downtime and treatment interruptions',
    icon: <EventNote />,
    color: '#ea580c',
    endpoint: '/reports/data/equipment_log_summary/',
    category: 'oncology',
  },
]

const REPORT_CATEGORIES = {
  standard: 'Standard Reports',
  specialized: 'Specialized Reports',
  oncology: 'Oncology',
}

const StatBox = ({ label, value, color = 'text.primary' }) => (
  <Box textAlign="center" p={2}>
    <Typography variant="h4" fontWeight={700} color={color}>
      {value}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
  </Box>
)

const DataBreakdown = ({ title, data, labelKey, valueKey = 'count' }) => {
  if (!data || data.length === 0) return null

  const total = data.reduce((sum, item) => sum + (item[valueKey] || 0), 0)

  return (
    <Box mt={3}>
      <Typography variant="subtitle2" fontWeight={600} mb={2}>
        {title}
      </Typography>
      {data.map((item, index) => {
        const label = item[labelKey] || '-'
        const value = item[valueKey] || 0
        const percentage = total > 0 ? (value / total) * 100 : 0

        return (
          <Box key={index} mb={1.5}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                {String(label).replace(/_/g, ' ')}
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {value} ({percentage.toFixed(1)}%)
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={percentage}
              sx={{
                height: 6,
                borderRadius: 1,
                backgroundColor: '#e2e8f0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#1B5E7A',
                },
              }}
            />
          </Box>
        )
      })}
    </Box>
  )
}

const ReportsList = () => {
  const { user } = useAuth()
  const { orgName, orgLogoUrl, systemSubtitle } = useBranding()
  const printRef = useRef(null)
  const [selectedReports, setSelectedReports] = useState(['all'])
  const [reportsData, setReportsData] = useState({})
  const [loadingReports, setLoadingReports] = useState({})
  const [days, setDays] = useState(30)

  // Get reports to display based on selection
  const getReportsToDisplay = () => {
    if (selectedReports.includes('all')) {
      return reportTypes
    }
    return reportTypes.filter((r) => selectedReports.includes(r.id))
  }

  const handleReportSelectionChange = (event) => {
    const value = event.target.value

    // Handle "All" logic
    if (value.includes('all') && !selectedReports.includes('all')) {
      setSelectedReports(['all'])
    } else if (selectedReports.includes('all') && value.length > 1) {
      setSelectedReports(value.filter((v) => v !== 'all'))
    } else if (value.length === 0) {
      setSelectedReports(['all'])
    } else {
      setSelectedReports(value)
    }
  }

  // Fetch data for a single report
  const fetchReportData = async (report) => {
    setLoadingReports((prev) => ({ ...prev, [report.id]: true }))
    try {
      const params = reportsWithDaysPeriod.includes(report.id) ? { days } : {}
      const response = await api.get(report.endpoint, { params })
      setReportsData((prev) => ({ ...prev, [report.id]: response.data }))
    } catch (err) {
      console.error(`Error fetching ${report.title}:`, err)
      toast.error(`Failed to load ${report.title}`)
      setReportsData((prev) => ({ ...prev, [report.id]: null }))
    } finally {
      setLoadingReports((prev) => ({ ...prev, [report.id]: false }))
    }
  }

  // Fetch all selected reports
  const fetchAllReports = async () => {
    const reportsToFetch = getReportsToDisplay()
    reportsToFetch.forEach((report) => {
      fetchReportData(report)
    })
  }

  // Fetch reports when selection changes
  useEffect(() => {
    const reportsToFetch = getReportsToDisplay()
    reportsToFetch.forEach((report) => {
      if (!reportsData[report.id]) {
        fetchReportData(report)
      }
    })
  }, [selectedReports])

  // Refresh all with new days period
  const handleRefresh = () => {
    setReportsData({})
    fetchAllReports()
  }

  const reportsWithDaysPeriod = [
    'maintenance_summary', 'work_order_analysis', 'cost_analysis',
    'downtime_analysis', 'pm_compliance', 'calibration_report',
    'departmental_costs', 'qa_compliance', 'equipment_log_summary',
    'vehicle_mileage_summary'
  ]

  const handleExportPDF = () => {
    const reportsToExport = getReportsToDisplay()
    if (reportsToExport.length === 0) return

    const printWindow = window.open('', '_blank')
    printWindow.document.write(`
      <html>
        <head>
          <title>Reports Export</title>
          <style>
            @page { margin: 15mm; }
            body {
              font-family: Arial, sans-serif;
              padding: 0;
              margin: 0;
              color: #333;
            }
            .print-header {
              text-align: center;
              margin-bottom: 20px;
              padding-bottom: 15px;
              border-bottom: 2px solid #1B5E7A;
            }
            .print-header img {
              height: 70px;
              margin-bottom: 10px;
            }
            .print-header h1 {
              color: #1B5E7A;
              font-size: 18px;
              margin: 5px 0;
            }
            .print-header p {
              font-size: 12px;
              color: #666;
              margin: 5px 0;
            }
            .report-section {
              margin-bottom: 30px;
              page-break-inside: avoid;
            }
            .section-header {
              background-color: #1B5E7A;
              color: white;
              padding: 10px 15px;
              font-size: 14px;
              font-weight: bold;
              margin-bottom: 15px;
            }
            .stat-row {
              display: flex;
              justify-content: space-around;
              margin-bottom: 20px;
              flex-wrap: wrap;
            }
            .stat-box {
              text-align: center;
              padding: 15px;
              min-width: 100px;
            }
            .stat-value {
              font-size: 20px;
              font-weight: bold;
              color: #1B5E7A;
            }
            .stat-label {
              font-size: 11px;
              color: #666;
              margin-top: 5px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 15px 0;
              font-size: 11px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 6px;
              text-align: left;
            }
            th {
              background-color: #1B5E7A;
              color: white;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .breakdown-title {
              font-size: 12px;
              font-weight: bold;
              margin: 15px 0 8px;
              color: #1B5E7A;
            }
            .breakdown-item {
              margin-bottom: 6px;
            }
            .breakdown-label {
              display: flex;
              justify-content: space-between;
              font-size: 11px;
              margin-bottom: 2px;
            }
            .breakdown-bar {
              height: 5px;
              background-color: #e2e8f0;
              border-radius: 3px;
              overflow: hidden;
            }
            .breakdown-fill {
              height: 100%;
              background-color: #1B5E7A;
            }
            .print-footer {
              margin-top: 30px;
              padding-top: 10px;
              border-top: 1px solid #ddd;
              display: flex;
              justify-content: space-between;
              font-size: 10px;
              color: #666;
            }
            @media print {
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            ${orgLogoUrl ? `<img src="${orgLogoUrl}" alt="${orgName}" />` : ''}
            <h1 style="margin-bottom:2px;">${orgName}</h1>
            <h2 style="color:#1B5E7A;font-size:16px;margin:0 0 6px;">${systemSubtitle}</h2>
            <p>Reports Summary</p>
            <p>Period: Last ${days} days | Generated: ${new Date().toLocaleDateString()}</p>
          </div>
          ${reportsToExport.map((report) => {
            const data = reportsData[report.id]
            if (!data) return ''
            return `
              <div class="report-section">
                <div class="section-header">${report.title}</div>
                ${generatePDFContentForReport(report, data)}
              </div>
            `
          }).join('')}
          <div class="print-footer">
            <span>Printed: ${new Date().toLocaleString()}</span>
            <span>Powered by DuoNep</span>
            <span>Printed by: ${user?.name || user?.email || 'System User'}</span>
          </div>
        </body>
      </html>
    `)
    printWindow.document.close()
    printWindow.focus()
    setTimeout(() => {
      printWindow.print()
      printWindow.close()
    }, 500)
  }

  const generatePDFContentForReport = (report, data) => {
    if (!data) return '<p>No data available</p>'

    switch (report.id) {
      case 'asset_summary':
        return `
          <div class="stat-row">
            <div class="stat-box">
              <div class="stat-value">${data.total || 0}</div>
              <div class="stat-label">Total Assets</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">${data.by_status?.find((s) => s.status === 'operational')?.count || 0}</div>
              <div class="stat-label">Operational</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">${data.by_status?.find((s) => s.status === 'under_maintenance')?.count || 0}</div>
              <div class="stat-label">Under Maintenance</div>
            </div>
          </div>
          ${generateBreakdownHTML('By Status', data.by_status, 'status')}
          ${generateBreakdownHTML('By Category', data.by_category, 'category__name')}
        `

      case 'maintenance_summary':
        return `
          <div class="stat-row">
            <div class="stat-box">
              <div class="stat-value">${data.total_work_orders || 0}</div>
              <div class="stat-label">Total Work Orders</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">${data.by_status?.find((s) => s.status === 'completed')?.count || 0}</div>
              <div class="stat-label">Completed</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">${data.avg_completion_hours?.toFixed(1) || '-'}</div>
              <div class="stat-label">Avg Hours</div>
            </div>
          </div>
          ${generateBreakdownHTML('By Status', data.by_status, 'status')}
          ${generateBreakdownHTML('By Priority', data.by_priority, 'priority')}
        `

      case 'work_order_analysis':
        return `
          <div class="stat-row">
            <div class="stat-box">
              <div class="stat-value">${data.total_work_orders || 0}</div>
              <div class="stat-label">Total</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">${data.completed || 0}</div>
              <div class="stat-label">Completed</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">${data.completion_rate || 0}%</div>
              <div class="stat-label">Completion Rate</div>
            </div>
          </div>
          ${generateBreakdownHTML('By Type', data.by_type, 'work_type')}
          ${generateBreakdownHTML('By Priority', data.by_priority, 'priority')}
        `

      case 'inventory_summary':
        return `
          <div class="stat-row">
            <div class="stat-box">
              <div class="stat-value">${data.total_parts || 0}</div>
              <div class="stat-label">Total Parts</div>
            </div>
            <div class="stat-box">
              <div class="stat-value" style="color: #ef4444;">${data.low_stock_count || 0}</div>
              <div class="stat-label">Low Stock</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">UGX ${(data.total_inventory_value || 0).toLocaleString()}</div>
              <div class="stat-label">Total Value</div>
            </div>
          </div>
        `

      case 'cost_analysis':
        return `
          <div class="stat-row">
            <div class="stat-box">
              <div class="stat-value" style="color: #ef4444;">UGX ${(data.total_maintenance_cost || 0).toLocaleString()}</div>
              <div class="stat-label">Total Cost</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">UGX ${(data.average_work_order_cost || 0).toLocaleString()}</div>
              <div class="stat-label">Average Cost</div>
            </div>
          </div>
          ${generateBreakdownHTML('Cost by Work Type', data.by_work_type, 'work_type', 'total')}
        `

      case 'vendor_performance':
        return `
          <div class="stat-row">
            <div class="stat-box">
              <div class="stat-value">${data.total_vendors || 0}</div>
              <div class="stat-label">Active Vendors</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">${data.active_contracts || 0}</div>
              <div class="stat-label">Active Contracts</div>
            </div>
            <div class="stat-box">
              <div class="stat-value" style="color: #f59e0b;">${data.expiring_soon || 0}</div>
              <div class="stat-label">Expiring Soon</div>
            </div>
          </div>
          ${generateBreakdownHTML('By Type', data.by_type, 'vendor_type')}
        `

      case 'downtime_analysis':
        return `
          <div class="stat-row">
            <div class="stat-box">
              <div class="stat-value" style="color: #dc2626;">${data.summary?.total_downtime_hours?.toFixed(1) || 0}h</div>
              <div class="stat-label">Total Downtime</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">${data.summary?.total_incidents || 0}</div>
              <div class="stat-label">Incidents</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">${data.summary?.critical_asset_downtime?.toFixed(1) || 0}h</div>
              <div class="stat-label">Critical Downtime</div>
            </div>
          </div>
          ${generateBreakdownHTML('By Section/Unit', data.by_department, 'department__name', 'total_downtime')}
        `

      case 'pm_compliance':
        return `
          <div class="stat-row">
            <div class="stat-box">
              <div class="stat-value">${data.summary?.total_scheduled || 0}</div>
              <div class="stat-label">Total PMs</div>
            </div>
            <div class="stat-box">
              <div class="stat-value" style="color: #10b981;">${data.summary?.completed || 0}</div>
              <div class="stat-label">Completed</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">${data.summary?.compliance_rate || 0}%</div>
              <div class="stat-label">Compliance Rate</div>
            </div>
            <div class="stat-box">
              <div class="stat-value" style="color: #ef4444;">${data.summary?.overdue || 0}</div>
              <div class="stat-label">Overdue</div>
            </div>
          </div>
        `

      case 'calibration_report':
        return `
          <div class="stat-row">
            <div class="stat-box">
              <div class="stat-value">${data.summary?.total_calibrations || 0}</div>
              <div class="stat-label">Total</div>
            </div>
            <div class="stat-box">
              <div class="stat-value" style="color: #10b981;">${data.summary?.passed || 0}</div>
              <div class="stat-label">Passed</div>
            </div>
            <div class="stat-box">
              <div class="stat-value" style="color: #ef4444;">${data.summary?.failed || 0}</div>
              <div class="stat-label">Failed</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">${data.summary?.pass_rate || 0}%</div>
              <div class="stat-label">Pass Rate</div>
            </div>
          </div>
        `

      case 'equipment_lifecycle':
        return `
          <div class="stat-row">
            <div class="stat-box">
              <div class="stat-value">${data.total_assets || 0}</div>
              <div class="stat-label">Total Assets</div>
            </div>
            <div class="stat-box">
              <div class="stat-value" style="color: #ef4444;">${data.summary?.assets_needing_replacement || 0}</div>
              <div class="stat-label">Need Replacement</div>
            </div>
            <div class="stat-box">
              <div class="stat-value" style="color: #f59e0b;">${data.summary?.warranties_expiring_90_days || 0}</div>
              <div class="stat-label">Warranties Expiring</div>
            </div>
          </div>
        `

      case 'departmental_costs':
        return `
          <div class="stat-row">
            <div class="stat-box">
              <div class="stat-value" style="color: #ef4444;">UGX ${(data.summary?.total_parts_cost || 0).toLocaleString()}</div>
              <div class="stat-label">Total Parts Cost</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">${data.summary?.total_work_orders || 0}</div>
              <div class="stat-label">Total Work Orders</div>
            </div>
          </div>
        `

      case 'high_cost_assets':
        return `
          <div class="stat-row">
            <div class="stat-box">
              <div class="stat-value">UGX ${(data.total_asset_investment || 0).toLocaleString()}</div>
              <div class="stat-label">Total Asset Investment</div>
            </div>
          </div>
        `

      case 'qa_compliance':
        return `
          <div class="stat-row">
            <div class="stat-box">
              <div class="stat-value">${data.summary?.total_qa_records || 0}</div>
              <div class="stat-label">Total QA</div>
            </div>
            <div class="stat-box">
              <div class="stat-value" style="color: #10b981;">${data.summary?.passed || 0}</div>
              <div class="stat-label">Passed</div>
            </div>
            <div class="stat-box">
              <div class="stat-value" style="color: #ef4444;">${data.summary?.failed || 0}</div>
              <div class="stat-label">Failed</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">${data.summary?.pass_rate || 0}%</div>
              <div class="stat-label">Pass Rate</div>
            </div>
          </div>
        `

      case 'radiation_source_inventory':
        return `
          <div class="stat-row">
            <div class="stat-box">
              <div class="stat-value">${data.summary?.total_active_sources || 0}</div>
              <div class="stat-label">Active Sources</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">${data.summary?.total_current_activity_gbq || 0} GBq</div>
              <div class="stat-label">Current Activity</div>
            </div>
            <div class="stat-box">
              <div class="stat-value" style="color: #ef4444;">${data.summary?.leak_tests_overdue || 0}</div>
              <div class="stat-label">Leak Tests Overdue</div>
            </div>
          </div>
        `

      case 'equipment_log_summary':
        return `
          <div class="stat-row">
            <div class="stat-box">
              <div class="stat-value">${data.summary?.total_logs || 0}</div>
              <div class="stat-label">Total Logs</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">${data.summary?.total_beam_on_hours || 0}h</div>
              <div class="stat-label">Beam On</div>
            </div>
            <div class="stat-box">
              <div class="stat-value" style="color: #ef4444;">${data.summary?.total_downtime_hours || 0}h</div>
              <div class="stat-label">Downtime</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">${data.summary?.availability_rate || 0}%</div>
              <div class="stat-label">Availability</div>
            </div>
          </div>
        `

      case 'vehicle_mileage_summary':
        return `
          <div class="stat-row">
            <div class="stat-box">
              <div class="stat-value" style="color: #0e7490;">${data.totals?.total_distance || 0}</div>
              <div class="stat-label">Total Distance (km)</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">${data.totals?.total_trips || 0}</div>
              <div class="stat-label">Total Trips</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">${data.totals?.avg_distance_per_trip || 0}</div>
              <div class="stat-label">Avg km / Trip</div>
            </div>
            <div class="stat-box">
              <div class="stat-value" style="color: #f59e0b;">${data.totals?.vehicles_due_service || 0}</div>
              <div class="stat-label">Vehicles Due Service</div>
            </div>
          </div>
          <div class="breakdown-title">By Vehicle</div>
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 16px;">
            <thead>
              <tr style="border-bottom: 2px solid #ddd; text-align: left;">
                <th style="padding: 6px;">Vehicle</th>
                <th style="padding: 6px; text-align: center;">Trips</th>
                <th style="padding: 6px; text-align: right;">Total km</th>
                <th style="padding: 6px; text-align: right;">Avg/Trip</th>
                <th style="padding: 6px; text-align: right;">Odometer</th>
                <th style="padding: 6px; text-align: center;">Service</th>
              </tr>
            </thead>
            <tbody>
              ${(data.by_vehicle || []).map((v) => `
                <tr style="border-bottom: 1px solid #eee;">
                  <td style="padding: 6px;">${v.vehicle_registration || '-'}${v.vehicle_name ? ` - ${v.vehicle_name}` : ''}</td>
                  <td style="padding: 6px; text-align: center;">${v.trips}</td>
                  <td style="padding: 6px; text-align: right;">${v.total_distance}</td>
                  <td style="padding: 6px; text-align: right;">${v.avg_distance_per_trip}</td>
                  <td style="padding: 6px; text-align: right;">${v.current_mileage ?? '-'}</td>
                  <td style="padding: 6px; text-align: center;">${v.due_for_service ? 'Due' : 'OK'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          ${(data.anomalies && data.anomalies.length > 0) ? `
            <div class="breakdown-title">Mileage Anomalies (odometer reading went backwards)</div>
            <table style="width: 100%; border-collapse: collapse;">
              <thead>
                <tr style="border-bottom: 2px solid #ddd; text-align: left;">
                  <th style="padding: 6px;">Request</th>
                  <th style="padding: 6px;">Vehicle</th>
                  <th style="padding: 6px; text-align: right;">Out</th>
                  <th style="padding: 6px; text-align: right;">Return</th>
                  <th style="padding: 6px;">Reason</th>
                </tr>
              </thead>
              <tbody>
                ${data.anomalies.map((a) => `
                  <tr style="border-bottom: 1px solid #eee; background: #fef3c7;">
                    <td style="padding: 6px;">${a.request_number || '-'}</td>
                    <td style="padding: 6px;">${a.vehicle_registration || '-'}</td>
                    <td style="padding: 6px; text-align: right;">${a.mileage_out}</td>
                    <td style="padding: 6px; text-align: right;">${a.mileage_return}</td>
                    <td style="padding: 6px;">${a.reason || ''}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          ` : ''}
        `

      default:
        return '<p>Report data available</p>'
    }
  }

  const generateBreakdownHTML = (title, data, labelKey, valueKey = 'count') => {
    if (!data || data.length === 0) return ''
    const total = data.reduce((sum, item) => sum + (item[valueKey] || 0), 0)

    return `
      <div class="breakdown-title">${title}</div>
      ${data.map((item) => {
        const label = item[labelKey] || '-'
        const value = item[valueKey] || 0
        const percentage = total > 0 ? (value / total) * 100 : 0
        return `
          <div class="breakdown-item">
            <div class="breakdown-label">
              <span style="text-transform: capitalize;">${String(label).replace(/_/g, ' ')}</span>
              <span>${value} (${percentage.toFixed(1)}%)</span>
            </div>
            <div class="breakdown-bar">
              <div class="breakdown-fill" style="width: ${percentage}%;"></div>
            </div>
          </div>
        `
      }).join('')}
    `
  }

  const formatCSVValue = (value) => {
    if (value === null || value === undefined) return '-'
    if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
      return `"${value.replace(/"/g, '""')}"`
    }
    return String(value).replace(/_/g, ' ')
  }

  const generateCSVForReport = (report, data) => {
    if (!data) return ''
    let csv = ''

    switch (report.id) {
      case 'asset_summary':
        csv += 'Metric,Value\n'
        csv += `Total Assets,${data.total || 0}\n\n`
        csv += 'Status,Count\n'
        data.by_status?.forEach((item) => {
          csv += `${formatCSVValue(item.status)},${item.count}\n`
        })
        csv += '\nCategory,Count\n'
        data.by_category?.forEach((item) => {
          csv += `${formatCSVValue(item.category__name)},${item.count}\n`
        })
        break

      case 'maintenance_summary':
        csv += 'Metric,Value\n'
        csv += `Total Work Orders,${data.total_work_orders || 0}\n`
        csv += `Average Completion Hours,${data.avg_completion_hours?.toFixed(1) || '-'}\n\n`
        csv += 'Status,Count\n'
        data.by_status?.forEach((item) => {
          csv += `${formatCSVValue(item.status)},${item.count}\n`
        })
        csv += '\nPriority,Count\n'
        data.by_priority?.forEach((item) => {
          csv += `${formatCSVValue(item.priority)},${item.count}\n`
        })
        break

      case 'work_order_analysis':
        csv += 'Metric,Value\n'
        csv += `Total Work Orders,${data.total_work_orders || 0}\n`
        csv += `Completed,${data.completed || 0}\n`
        csv += `Completion Rate,${data.completion_rate || 0}%\n\n`
        csv += 'Work Type,Count\n'
        data.by_type?.forEach((item) => {
          csv += `${formatCSVValue(item.work_type)},${item.count}\n`
        })
        csv += '\nPriority,Count\n'
        data.by_priority?.forEach((item) => {
          csv += `${formatCSVValue(item.priority)},${item.count}\n`
        })
        csv += '\nStatus,Count\n'
        data.by_status?.forEach((item) => {
          csv += `${formatCSVValue(item.status)},${item.count}\n`
        })
        break

      case 'inventory_summary':
        csv += 'Metric,Value\n'
        csv += `Total Parts,${data.total_parts || 0}\n`
        csv += `Low Stock Items,${data.low_stock_count || 0}\n`
        csv += `Total Inventory Value,${data.total_inventory_value || 0}\n`
        break

      case 'cost_analysis':
        csv += 'Metric,Value\n'
        csv += `Total Maintenance Cost,${data.total_maintenance_cost || 0}\n`
        csv += `Average Work Order Cost,${data.average_work_order_cost || 0}\n\n`
        csv += 'Work Type,Count,Total Cost\n'
        data.by_work_type?.forEach((item) => {
          csv += `${formatCSVValue(item.work_type)},${item.count},${item.total || 0}\n`
        })
        break

      case 'vendor_performance':
        csv += 'Metric,Value\n'
        csv += `Total Active Vendors,${data.total_vendors || 0}\n`
        csv += `Active Contracts,${data.active_contracts || 0}\n`
        csv += `Expiring Soon,${data.expiring_soon || 0}\n\n`
        csv += 'Vendor Type,Count\n'
        data.by_type?.forEach((item) => {
          csv += `${formatCSVValue(item.vendor_type)},${item.count}\n`
        })
        csv += '\nTop Vendors\nName,Type,Rating\n'
        data.top_vendors?.forEach((v) => {
          csv += `${formatCSVValue(v.name)},${formatCSVValue(v.vendor_type)},${v.rating}\n`
        })
        break

      case 'vehicle_mileage_summary':
        csv += 'Metric,Value\n'
        csv += `Total Distance (km),${data.totals?.total_distance || 0}\n`
        csv += `Total Trips,${data.totals?.total_trips || 0}\n`
        csv += `Avg km per Trip,${data.totals?.avg_distance_per_trip || 0}\n`
        csv += `Vehicles Due Service,${data.totals?.vehicles_due_service || 0}\n`
        csv += `Anomalies,${data.totals?.anomaly_count || 0}\n\n`
        csv += 'By Vehicle\nVehicle,Name,Trips,Total km,Avg km/Trip,Odometer,Since Service,Due for Service\n'
        data.by_vehicle?.forEach((v) => {
          csv += `${formatCSVValue(v.vehicle_registration)},${formatCSVValue(v.vehicle_name)},${v.trips},${v.total_distance},${v.avg_distance_per_trip},${v.current_mileage ?? ''},${v.km_since_service ?? ''},${v.due_for_service ? 'Yes' : 'No'}\n`
        })
        csv += '\nAnomalies\nRequest,Vehicle,Out,Return,Reason\n'
        data.anomalies?.forEach((a) => {
          csv += `${formatCSVValue(a.request_number)},${formatCSVValue(a.vehicle_registration)},${a.mileage_out},${a.mileage_return},${formatCSVValue(a.reason)}\n`
        })
        break

      case 'downtime_analysis':
        csv += 'Metric,Value\n'
        csv += `Total Downtime Hours,${data.summary?.total_downtime_hours?.toFixed(1) || 0}\n`
        csv += `Total Incidents,${data.summary?.total_incidents || 0}\n`
        csv += `Critical Asset Downtime,${data.summary?.critical_asset_downtime?.toFixed(1) || 0}\n\n`
        csv += 'Department,Downtime Hours,Incidents\n'
        data.by_department?.forEach((item) => {
          csv += `${formatCSVValue(item.department__name)},${item.total_downtime || 0},${item.incident_count || 0}\n`
        })
        break

      case 'pm_compliance':
        csv += 'Metric,Value\n'
        csv += `Total Scheduled,${data.summary?.total_scheduled || 0}\n`
        csv += `Completed,${data.summary?.completed || 0}\n`
        csv += `Compliance Rate,${data.summary?.compliance_rate || 0}%\n`
        csv += `Overdue,${data.summary?.overdue || 0}\n\n`
        csv += 'Template,Frequency,Total,Completed,Overdue\n'
        data.by_template?.forEach((item) => {
          csv += `${formatCSVValue(item.template__name)},${formatCSVValue(item.template__frequency)},${item.total},${item.completed},${item.overdue}\n`
        })
        break

      case 'calibration_report':
        csv += 'Metric,Value\n'
        csv += `Total Calibrations,${data.summary?.total_calibrations || 0}\n`
        csv += `Passed,${data.summary?.passed || 0}\n`
        csv += `Failed,${data.summary?.failed || 0}\n`
        csv += `Pass Rate,${data.summary?.pass_rate || 0}%\n\n`
        csv += 'Overdue Calibrations\nAsset,Category,Certificate #,Due Date\n'
        data.overdue_calibrations?.forEach((item) => {
          csv += `${formatCSVValue(item.asset__name)},${formatCSVValue(item.asset__category__name)},${formatCSVValue(item.certificate_number)},${item.next_calibration_date}\n`
        })
        csv += '\nUpcoming Calibrations\nAsset,Category,Certificate #,Due Date\n'
        data.upcoming_calibrations?.forEach((item) => {
          csv += `${formatCSVValue(item.asset__name)},${formatCSVValue(item.asset__category__name)},${formatCSVValue(item.certificate_number)},${item.next_calibration_date}\n`
        })
        break

      case 'equipment_lifecycle':
        csv += 'Metric,Value\n'
        csv += `Total Assets,${data.total_assets || 0}\n`
        csv += `Needing Replacement,${data.summary?.assets_needing_replacement || 0}\n`
        csv += `Warranties Expiring (90 days),${data.summary?.warranties_expiring_90_days || 0}\n\n`
        csv += 'Age Distribution\nAge Range,Count\n'
        if (data.age_distribution) {
          Object.entries(data.age_distribution).forEach(([range, count]) => {
            csv += `${range},${count}\n`
          })
        }
        csv += '\nReplacement Forecast\nAsset,Age (years),Remaining (years)\n'
        data.replacement_forecast?.forEach((item) => {
          csv += `${formatCSVValue(item.name)},${item.age_years},${item.remaining_years}\n`
        })
        break

      case 'departmental_costs':
        csv += 'Metric,Value\n'
        csv += `Total Parts Cost,${data.summary?.total_parts_cost || 0}\n`
        csv += `Total Work Orders,${data.summary?.total_work_orders || 0}\n\n`
        csv += 'Department,Assets,Work Orders,Parts Cost\n'
        data.departments?.forEach((dept) => {
          csv += `${formatCSVValue(dept.name)},${dept.asset_count},${dept.work_order_count},${dept.parts_cost || 0}\n`
        })
        break

      case 'high_cost_assets':
        csv += 'Metric,Value\n'
        csv += `Total Asset Investment,${data.total_asset_investment || 0}\n\n`
        csv += 'Highest Purchase Cost\nAsset,Category,Cost\n'
        data.top_by_purchase_cost?.forEach((item) => {
          csv += `${formatCSVValue(item.name)},${formatCSVValue(item.category__name)},${item.purchase_cost || 0}\n`
        })
        csv += '\nHighest Maintenance Cost\nAsset,Work Orders,Cost\n'
        data.top_by_maintenance_cost?.forEach((item) => {
          csv += `${formatCSVValue(item.name)},${item.work_order_count},${item.total_parts_cost || 0}\n`
        })
        break

      case 'qa_compliance':
        csv += 'Metric,Value\n'
        csv += `Total QA Records,${data.summary?.total_qa_records || 0}\n`
        csv += `Passed,${data.summary?.passed || 0}\n`
        csv += `Failed,${data.summary?.failed || 0}\n`
        csv += `Pass Rate,${data.summary?.pass_rate || 0}%\n\n`
        csv += 'QA Type,Total,Passed,Failed\n'
        data.by_type?.forEach((item) => {
          csv += `${formatCSVValue(item.qa_type)},${item.total},${item.passed},${item.failed}\n`
        })
        break

      case 'radiation_source_inventory':
        csv += 'Metric,Value\n'
        csv += `Active Sources,${data.summary?.total_active_sources || 0}\n`
        csv += `Total Current Activity (GBq),${data.summary?.total_current_activity_gbq || 0}\n`
        csv += `Leak Tests Overdue,${data.summary?.leak_tests_overdue || 0}\n\n`
        csv += 'By Isotope\nIsotope,Count,Total Activity\n'
        data.by_isotope?.forEach((item) => {
          csv += `${formatCSVValue(item.isotope)},${item.count},${item.total_activity}\n`
        })
        break

      case 'equipment_log_summary':
        csv += 'Metric,Value\n'
        csv += `Total Logs,${data.summary?.total_logs || 0}\n`
        csv += `Beam On Hours,${data.summary?.total_beam_on_hours || 0}\n`
        csv += `Downtime Hours,${data.summary?.total_downtime_hours || 0}\n`
        csv += `Availability Rate,${data.summary?.availability_rate || 0}%\n`
        csv += `Treatment Interruptions,${data.summary?.treatment_interruptions || 0}\n\n`
        csv += 'Log Type,Count\n'
        data.by_type?.forEach((item) => {
          csv += `${formatCSVValue(item.log_type)},${item.count}\n`
        })
        break

      default:
        csv += 'Data not formatted for CSV export\n'
    }

    return csv
  }

  const handleExportCSV = () => {
    const reportsToExport = getReportsToDisplay()
    if (reportsToExport.length === 0) return

    let csvContent = 'DuoNep Reports Export\n'
    csvContent += `Generated,${new Date().toLocaleString()}\n`
    csvContent += `Period,Last ${days} days\n`

    reportsToExport.forEach((report) => {
      const data = reportsData[report.id]
      if (!data) return

      csvContent += `\n${'='.repeat(50)}\n`
      csvContent += `${report.title}\n`
      csvContent += `${'='.repeat(50)}\n\n`
      csvContent += generateCSVForReport(report, data)
    })

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `cmms_reports_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  // Render report section content
  const renderReportSection = (report) => {
    const data = reportsData[report.id]
    const isLoading = loadingReports[report.id]

    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress size={24} sx={{ mr: 2 }} />
          <Typography color="text.secondary">Loading {report.title}...</Typography>
        </Box>
      )
    }

    if (!data) {
      return (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">Failed to load report data</Typography>
          <Button size="small" onClick={() => fetchReportData(report)} sx={{ mt: 1 }}>
            Retry
          </Button>
        </Box>
      )
    }

    switch (report.id) {
      case 'asset_summary':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={4}>
                <StatBox label="Total Assets" value={data.total} color="#1B5E7A" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox
                  label="Operational"
                  value={data.by_status?.find((s) => s.status === 'operational')?.count || 0}
                  color="#10b981"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox
                  label="Under Maintenance"
                  value={data.by_status?.find((s) => s.status === 'under_maintenance')?.count || 0}
                  color="#f59e0b"
                />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DataBreakdown title="By Status" data={data.by_status} labelKey="status" />
              </Grid>
              <Grid item xs={12} md={6}>
                <DataBreakdown title="By Category" data={data.by_category} labelKey="category__name" />
              </Grid>
            </Grid>
          </>
        )

      case 'maintenance_summary':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={3}>
                <StatBox label="Total Work Orders" value={data.total_work_orders} color="#1B5E7A" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox
                  label="Completed"
                  value={data.by_status?.find((s) => s.status === 'completed')?.count || 0}
                  color="#10b981"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox
                  label="In Progress"
                  value={data.by_status?.find((s) => s.status === 'in_progress')?.count || 0}
                  color="#3b82f6"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox
                  label="Avg Hours"
                  value={data.avg_completion_hours?.toFixed(1) || '-'}
                  color="#8b5cf6"
                />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DataBreakdown title="By Status" data={data.by_status} labelKey="status" />
              </Grid>
              <Grid item xs={12} md={6}>
                <DataBreakdown title="By Priority" data={data.by_priority} labelKey="priority" />
              </Grid>
            </Grid>
          </>
        )

      case 'work_order_analysis':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={3}>
                <StatBox label="Total" value={data.total_work_orders} color="#1B5E7A" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Completed" value={data.completed} color="#10b981" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Completion Rate" value={`${data.completion_rate}%`} color="#3b82f6" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Period" value={`${data.period_days} days`} color="#8b5cf6" />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <DataBreakdown title="By Type" data={data.by_type} labelKey="work_type" />
              </Grid>
              <Grid item xs={12} md={4}>
                <DataBreakdown title="By Priority" data={data.by_priority} labelKey="priority" />
              </Grid>
              <Grid item xs={12} md={4}>
                <DataBreakdown title="By Status" data={data.by_status} labelKey="status" />
              </Grid>
            </Grid>
          </>
        )

      case 'inventory_summary':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={4}>
                <StatBox label="Total Parts" value={data.total_parts} color="#1B5E7A" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox label="Low Stock Items" value={data.low_stock_count} color="#ef4444" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox
                  label="Total Value"
                  value={`UGX ${(data.total_inventory_value || 0).toLocaleString()}`}
                  color="#10b981"
                />
              </Grid>
            </Grid>
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {data.low_stock_count > 0
                  ? `${data.low_stock_count} items need to be reordered`
                  : 'All inventory levels are adequate'}
              </Typography>
            </Box>
          </>
        )

      case 'cost_analysis':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={4}>
                <StatBox
                  label="Total Cost"
                  value={`UGX ${(data.total_maintenance_cost || 0).toLocaleString()}`}
                  color="#ef4444"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox
                  label="Average Cost"
                  value={`UGX ${(data.average_work_order_cost || 0).toLocaleString()}`}
                  color="#f59e0b"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox label="Period" value={`${data.period_days} days`} color="#1B5E7A" />
              </Grid>
            </Grid>
            <Divider />
            <DataBreakdown
              title="Cost by Work Type"
              data={data.by_work_type}
              labelKey="work_type"
              valueKey="total"
            />
          </>
        )

      case 'vendor_performance':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={4}>
                <StatBox label="Active Vendors" value={data.total_vendors} color="#1B5E7A" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox label="Active Contracts" value={data.active_contracts} color="#10b981" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox label="Expiring Soon" value={data.expiring_soon} color="#f59e0b" />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DataBreakdown title="By Type" data={data.by_type} labelKey="vendor_type" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>
                    Top Vendors
                  </Typography>
                  {data.top_vendors?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Vendor</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align="center">Rating</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.top_vendors.map((vendor) => (
                            <TableRow key={vendor.id}>
                              <TableCell>{vendor.name}</TableCell>
                              <TableCell sx={{ textTransform: 'capitalize' }}>
                                {vendor.vendor_type?.replace(/_/g, ' ')}
                              </TableCell>
                              <TableCell align="center">{vendor.rating}/5</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No vendor data available
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </>
        )

      case 'vehicle_mileage_summary':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={3}>
                <StatBox label="Total Distance (km)" value={data.totals?.total_distance ?? 0} color="#0e7490" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Total Trips" value={data.totals?.total_trips ?? 0} color="#1B5E7A" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Avg km / Trip" value={data.totals?.avg_distance_per_trip ?? 0} color="#10b981" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Vehicles Due Service" value={data.totals?.vehicles_due_service ?? 0} color="#f59e0b" />
              </Grid>
            </Grid>
            <Divider sx={{ mb: 2 }} />
            <Box mb={3}>
              <Typography variant="subtitle2" fontWeight={600} mb={2}>
                By Vehicle
              </Typography>
              {data.by_vehicle?.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Vehicle</TableCell>
                        <TableCell align="center">Trips</TableCell>
                        <TableCell align="right">Total km</TableCell>
                        <TableCell align="right">Avg km / Trip</TableCell>
                        <TableCell align="right">Odometer</TableCell>
                        <TableCell align="right">Since Service</TableCell>
                        <TableCell align="center">Service</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.by_vehicle.map((v) => (
                        <TableRow key={v.vehicle_registration}>
                          <TableCell>
                            {v.vehicle_registration}
                            {v.vehicle_name ? ` - ${v.vehicle_name}` : ''}
                          </TableCell>
                          <TableCell align="center">{v.trips}</TableCell>
                          <TableCell align="right">{v.total_distance}</TableCell>
                          <TableCell align="right">{v.avg_distance_per_trip}</TableCell>
                          <TableCell align="right">{v.current_mileage ?? '-'}</TableCell>
                          <TableCell align="right">{v.km_since_service ?? '-'}</TableCell>
                          <TableCell align="center">
                            {v.due_for_service ? (
                              <Chip label="Due" color="warning" size="small" />
                            ) : (
                              <Chip label="OK" color="success" size="small" variant="outlined" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No completed trips in this period
                </Typography>
              )}
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} mb={2}>
                Mileage Anomalies{' '}
                <Typography component="span" variant="caption" color="text.secondary">
                  (odometer reading went backwards)
                </Typography>
              </Typography>
              {data.anomalies?.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Request</TableCell>
                        <TableCell>Vehicle</TableCell>
                        <TableCell align="right">Out</TableCell>
                        <TableCell align="right">Return</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.anomalies.map((a, idx) => (
                        <TableRow key={`${a.request_number}-${idx}`} sx={{ backgroundColor: 'rgba(245, 158, 11, 0.08)' }}>
                          <TableCell>{a.request_number}</TableCell>
                          <TableCell>{a.vehicle_registration}</TableCell>
                          <TableCell align="right">{a.mileage_out}</TableCell>
                          <TableCell align="right">{a.mileage_return}</TableCell>
                          <TableCell>{a.reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No mileage anomalies detected
                </Typography>
              )}
            </Box>
          </>
        )

      case 'downtime_analysis':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={3}>
                <StatBox label="Total Downtime" value={`${data.summary?.total_downtime_hours?.toFixed(1) || 0}h`} color="#dc2626" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Incidents" value={data.summary?.total_incidents || 0} color="#1B5E7A" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Critical Downtime" value={`${data.summary?.critical_asset_downtime?.toFixed(1) || 0}h`} color="#f59e0b" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Critical Incidents" value={data.summary?.critical_incidents || 0} color="#ef4444" />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DataBreakdown title="By Section/Unit" data={data.by_department} labelKey="department__name" valueKey="total_downtime" />
              </Grid>
              <Grid item xs={12} md={6}>
                <DataBreakdown title="By Category" data={data.by_category} labelKey="category" valueKey="total_downtime" />
              </Grid>
            </Grid>
          </>
        )

      case 'pm_compliance':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={3}>
                <StatBox label="Total PMs" value={data.summary?.total_scheduled || 0} color="#1B5E7A" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Completed" value={data.summary?.completed || 0} color="#10b981" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Compliance Rate" value={`${data.summary?.compliance_rate || 0}%`} color="#3b82f6" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Overdue" value={data.summary?.overdue || 0} color="#ef4444" />
              </Grid>
            </Grid>
            <Divider />
            <Box mt={3}>
              <Typography variant="subtitle2" fontWeight={600} mb={2}>By PM Template</Typography>
              {data.by_template?.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Template</TableCell>
                        <TableCell>Frequency</TableCell>
                        <TableCell align="center">Total</TableCell>
                        <TableCell align="center">Completed</TableCell>
                        <TableCell align="center">Overdue</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.by_template.map((item, i) => (
                        <TableRow key={i}>
                          <TableCell>{item.template__name || '-'}</TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{item.template__frequency}</TableCell>
                          <TableCell align="center">{item.total}</TableCell>
                          <TableCell align="center">{item.completed}</TableCell>
                          <TableCell align="center" sx={{ color: item.overdue > 0 ? 'error.main' : 'inherit' }}>{item.overdue}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : <Typography variant="body2" color="text.secondary">No PM data available</Typography>}
            </Box>
          </>
        )

      case 'calibration_report':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={2}>
                <StatBox label="Total" value={data.summary?.total_calibrations || 0} color="#1B5E7A" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Passed" value={data.summary?.passed || 0} color="#10b981" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Failed" value={data.summary?.failed || 0} color="#ef4444" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Pass Rate" value={`${data.summary?.pass_rate || 0}%`} color="#3b82f6" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Overdue" value={data.summary?.overdue_count || 0} color="#f59e0b" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Due Soon" value={data.summary?.upcoming_30_days || 0} color="#8b5cf6" />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>Overdue Calibrations</Typography>
                  {data.overdue_calibrations?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Asset</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Certificate #</TableCell>
                            <TableCell>Due Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.overdue_calibrations.slice(0, 10).map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.asset__name}</TableCell>
                              <TableCell>{item.asset__category__name}</TableCell>
                              <TableCell>{item.certificate_number || '-'}</TableCell>
                              <TableCell sx={{ color: 'error.main' }}>{item.next_calibration_date}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : <Typography variant="body2" color="text.secondary">No overdue calibrations</Typography>}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>Upcoming Calibrations (30 days)</Typography>
                  {data.upcoming_calibrations?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Asset</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Certificate #</TableCell>
                            <TableCell>Due Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.upcoming_calibrations.slice(0, 10).map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.asset__name}</TableCell>
                              <TableCell>{item.asset__category__name}</TableCell>
                              <TableCell>{item.certificate_number || '-'}</TableCell>
                              <TableCell>{item.next_calibration_date}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : <Typography variant="body2" color="text.secondary">No upcoming calibrations</Typography>}
                </Box>
              </Grid>
            </Grid>
          </>
        )

      case 'equipment_lifecycle':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={4}>
                <StatBox label="Total Assets" value={data.total_assets || 0} color="#1B5E7A" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox label="Needing Replacement" value={data.summary?.assets_needing_replacement || 0} color="#ef4444" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox label="Warranties Expiring" value={data.summary?.warranties_expiring_90_days || 0} color="#f59e0b" />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>Age Distribution</Typography>
                  {data.age_distribution && Object.entries(data.age_distribution).map(([range, count]) => (
                    <Box key={range} mb={1.5}>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2">{range}</Typography>
                        <Typography variant="body2" fontWeight={500}>{count}</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={(count / (data.total_assets || 1)) * 100}
                        sx={{ height: 6, borderRadius: 1, backgroundColor: '#e2e8f0', '& .MuiLinearProgress-bar': { backgroundColor: '#1B5E7A' } }} />
                    </Box>
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>Replacement Forecast</Typography>
                  {data.replacement_forecast?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Asset</TableCell>
                            <TableCell>Age</TableCell>
                            <TableCell>Remaining</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.replacement_forecast.slice(0, 8).map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.name}</TableCell>
                              <TableCell>{item.age_years}y</TableCell>
                              <TableCell sx={{ color: item.remaining_years < 1 ? 'error.main' : 'warning.main' }}>{item.remaining_years}y</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : <Typography variant="body2" color="text.secondary">No assets needing replacement</Typography>}
                </Box>
              </Grid>
            </Grid>
          </>
        )

      case 'departmental_costs':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={4}>
                <StatBox label="Total Parts Cost" value={`UGX ${(data.summary?.total_parts_cost || 0).toLocaleString()}`} color="#ef4444" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox label="Total Work Orders" value={data.summary?.total_work_orders || 0} color="#1B5E7A" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatBox label="Period" value={`${data.period_days} days`} color="#8b5cf6" />
              </Grid>
            </Grid>
            <Divider />
            <Box mt={3}>
              <Typography variant="subtitle2" fontWeight={600} mb={2}>Costs by Section/Unit</Typography>
              {data.departments?.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Section/Unit</TableCell>
                        <TableCell align="center">Assets</TableCell>
                        <TableCell align="center">Work Orders</TableCell>
                        <TableCell align="right">Parts Cost</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.departments.map((dept) => (
                        <TableRow key={dept.id}>
                          <TableCell>{dept.name}</TableCell>
                          <TableCell align="center">{dept.asset_count}</TableCell>
                          <TableCell align="center">{dept.work_order_count}</TableCell>
                          <TableCell align="right">UGX {(dept.parts_cost || 0).toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : <Typography variant="body2" color="text.secondary">No section/unit data available</Typography>}
            </Box>
          </>
        )

      case 'high_cost_assets':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12}>
                <StatBox label="Total Asset Investment" value={`UGX ${(data.total_asset_investment || 0).toLocaleString()}`} color="#1B5E7A" />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>Highest Purchase Cost</Typography>
                  {data.top_by_purchase_cost?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Asset</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell align="right">Cost</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.top_by_purchase_cost.slice(0, 10).map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.name}</TableCell>
                              <TableCell>{item.category__name}</TableCell>
                              <TableCell align="right">UGX {(item.purchase_cost || 0).toLocaleString()}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : <Typography variant="body2" color="text.secondary">No data available</Typography>}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>Highest Maintenance Cost</Typography>
                  {data.top_by_maintenance_cost?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Asset</TableCell>
                            <TableCell align="center">WOs</TableCell>
                            <TableCell align="right">Cost</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.top_by_maintenance_cost.slice(0, 10).map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.name}</TableCell>
                              <TableCell align="center">{item.work_order_count}</TableCell>
                              <TableCell align="right">UGX {(item.total_parts_cost || 0).toLocaleString()}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : <Typography variant="body2" color="text.secondary">No data available</Typography>}
                </Box>
              </Grid>
            </Grid>
          </>
        )

      case 'qa_compliance':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={2}>
                <StatBox label="Total QA" value={data.summary?.total_qa_records || 0} color="#1B5E7A" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Passed" value={data.summary?.passed || 0} color="#10b981" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Failed" value={data.summary?.failed || 0} color="#ef4444" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Conditional" value={data.summary?.conditional || 0} color="#f59e0b" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Pass Rate" value={`${data.summary?.pass_rate || 0}%`} color="#3b82f6" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Pending" value={data.summary?.pending_review || 0} color="#8b5cf6" />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DataBreakdown title="By QA Type" data={data.by_type} labelKey="qa_type" valueKey="total" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>Failed Records</Typography>
                  {data.failed_records?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Asset</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.failed_records.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.asset__name}</TableCell>
                              <TableCell sx={{ textTransform: 'capitalize' }}>{item.qa_type?.replace('_', ' ')}</TableCell>
                              <TableCell>{item.qa_date}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : <Typography variant="body2" color="text.secondary">No failed QA records</Typography>}
                </Box>
              </Grid>
            </Grid>
          </>
        )

      case 'radiation_source_inventory':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={3}>
                <StatBox label="Active Sources" value={data.summary?.total_active_sources || 0} color="#1B5E7A" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Initial Activity" value={`${data.summary?.total_initial_activity_gbq || 0} GBq`} color="#10b981" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Current Activity" value={`${data.summary?.total_current_activity_gbq || 0} GBq`} color="#3b82f6" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatBox label="Leak Tests Overdue" value={data.summary?.leak_tests_overdue || 0} color="#ef4444" />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DataBreakdown title="By Isotope" data={data.by_isotope} labelKey="isotope" valueKey="count" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>Leak Test Due</Typography>
                  {data.leak_test_due?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Source ID</TableCell>
                            <TableCell>Isotope</TableCell>
                            <TableCell>Last Test</TableCell>
                            <TableCell>Days Ago</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.leak_test_due.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.source_id}</TableCell>
                              <TableCell>{item.isotope}</TableCell>
                              <TableCell>{item.last_leak_test || 'Never'}</TableCell>
                              <TableCell sx={{ color: 'error.main' }}>{item.days_since_leak_test || 'N/A'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : <Typography variant="body2" color="text.secondary">No overdue leak tests</Typography>}
                </Box>
              </Grid>
            </Grid>
          </>
        )

      case 'equipment_log_summary':
        return (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={2}>
                <StatBox label="Total Logs" value={data.summary?.total_logs || 0} color="#1B5E7A" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Beam On" value={`${data.summary?.total_beam_on_hours || 0}h`} color="#10b981" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Downtime" value={`${data.summary?.total_downtime_hours || 0}h`} color="#ef4444" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Availability" value={`${data.summary?.availability_rate || 0}%`} color="#3b82f6" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Interruptions" value={data.summary?.treatment_interruptions || 0} color="#f59e0b" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <StatBox label="Affected" value={data.summary?.treatments_affected || 0} color="#8b5cf6" />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DataBreakdown title="By Log Type" data={data.by_type} labelKey="log_type" valueKey="count" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>Error Logs</Typography>
                  {data.error_logs?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Asset</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Downtime</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.error_logs.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.asset__name}</TableCell>
                              <TableCell>{new Date(item.log_date).toLocaleDateString()}</TableCell>
                              <TableCell>{item.downtime_minutes}m</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : <Typography variant="body2" color="text.secondary">No error logs</Typography>}
                </Box>
              </Grid>
            </Grid>
          </>
        )

      default:
        return (
          <Box>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>{JSON.stringify(data, null, 2)}</pre>
          </Box>
        )
    }
  }

  const reportsToDisplay = getReportsToDisplay()

  // Group reports by category for dropdown
  const groupedReportOptions = Object.entries(REPORT_CATEGORIES).map(([key, label]) => ({
    category: key,
    label,
    reports: reportTypes.filter((r) => r.category === key),
  }))

  return (
    <Box>
      <PageHeader
        title="Reports"
        subtitle="Generate and view maintenance reports"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Reports' },
        ]}
        actions={
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              size="small"
              label="Period"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value={7}>Last 7 days</MenuItem>
              <MenuItem value={30}>Last 30 days</MenuItem>
              <MenuItem value={90}>Last 90 days</MenuItem>
              <MenuItem value={365}>Last year</MenuItem>
            </TextField>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdf />}
              onClick={handleExportPDF}
              disabled={reportsToDisplay.length === 0}
            >
              Export PDF
            </Button>
            <Button
              startIcon={<Download />}
              onClick={handleExportCSV}
              disabled={reportsToDisplay.length === 0}
            >
              Export CSV
            </Button>
          </Stack>
        }
      />

      {/* Report Selection Dropdown */}
      <Card sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <FilterList color="action" />
          <FormControl sx={{ minWidth: { xs: '100%', sm: 350 }, flex: 1 }}>
            <InputLabel>Select Reports</InputLabel>
            <Select
              multiple
              value={selectedReports}
              onChange={handleReportSelectionChange}
              input={<OutlinedInput label="Select Reports" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.includes('all') ? (
                    <Chip label="All Reports" size="small" color="primary" />
                  ) : (
                    selected.map((id) => {
                      const report = reportTypes.find((r) => r.id === id)
                      return report ? (
                        <Chip key={id} label={report.title} size="small" />
                      ) : null
                    })
                  )}
                </Box>
              )}
            >
              <MenuItem value="all">
                <Checkbox checked={selectedReports.includes('all')} />
                <ListItemText primary="All Reports" />
              </MenuItem>
              <Divider />
              {groupedReportOptions.map((group) => [
                <ListSubheader key={group.category} sx={{ backgroundColor: '#f5f5f5', fontWeight: 600 }}>
                  {group.label}
                </ListSubheader>,
                ...group.reports.map((report) => (
                  <MenuItem key={report.id} value={report.id} sx={{ pl: 4 }}>
                    <Checkbox checked={selectedReports.includes('all') || selectedReports.includes(report.id)} />
                    <ListItemText primary={report.title} secondary={report.description} />
                  </MenuItem>
                )),
              ])}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            {selectedReports.includes('all')
              ? `Showing all ${reportTypes.length} reports`
              : `Showing ${selectedReports.length} selected report${selectedReports.length !== 1 ? 's' : ''}`}
          </Typography>
        </Box>
      </Card>

      {/* Report Sections */}
      <Box ref={printRef}>
        {reportsToDisplay.length === 0 ? (
          <Card>
            <Box textAlign="center" py={6}>
              <Assessment sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No reports selected
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select reports from the dropdown above to view them
              </Typography>
            </Box>
          </Card>
        ) : (
          reportsToDisplay.map((report) => (
            <Card key={report.id} sx={{ mb: 3 }}>
              <Box
                display="flex"
                alignItems="center"
                gap={2}
                mb={3}
                pb={2}
                borderBottom="1px solid"
                borderColor="divider"
              >
                <Box
                  sx={{
                    backgroundColor: `${report.color}15`,
                    color: report.color,
                    p: 1,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {report.icon}
                </Box>
                <Box flex={1}>
                  <Typography variant="h6" fontWeight={600}>
                    {report.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {report.description}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  startIcon={<Refresh />}
                  onClick={() => fetchReportData(report)}
                  disabled={loadingReports[report.id]}
                >
                  Refresh
                </Button>
              </Box>
              {renderReportSection(report)}
            </Card>
          ))
        )}
      </Box>
    </Box>
  )
}

export default ReportsList
